// Repo Governance — security findings across governed repositories.
//
// Default view: open findings on default branches, worst first. Secrets in
// snippets are masked by the scanner; nothing here can reveal the value.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import * as React from "react";

import { SeverityBadge, When } from "@/components/governance/bits";
import { useRepoChecks } from "@/components/governance/checklist-dialog";
import { GovernanceHeader, useIsGovernanceAdmin } from "@/components/governance/nav";
import {
  Badge,
  Card,
  CardContent,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  selectClass,
} from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import {
  getSecurityAiReview,
  getSecuritySummary,
  listRepoScans,
  listSecurityFindings,
  rerunSecurityAiReview,
  setSecurityAiReview,
  updateSecurityFinding,
} from "@/lib/api/endpoints";
import { formatTokens, formatUsd } from "@/lib/format-usage";
import { useSearchParams } from "@/lib/navigation";
import { useGovernedRepos } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";
import { cn } from "@/lib/utils";
import type {
  GovernanceSeverity,
  RepoScanView,
  SecurityFindingStatus,
  SecurityFindingView,
} from "@/types/api";

const SEVERITIES: GovernanceSeverity[] = ["critical", "high", "medium", "low", "info"];
const STATUS_LABEL: Record<SecurityFindingStatus, string> = {
  open: "Open",
  acknowledged: "Acknowledged",
  false_positive: "False positive",
  resolved: "Resolved",
  fixed: "Fixed",
};
const filterSelect = cn(selectClass, "h-9 w-auto");

export default function SecurityFindingsPage() {
  const params = useSearchParams();
  const [repoId, setRepoId] = React.useState<number | undefined>(
    params.get("repo") ? Number(params.get("repo")) : undefined,
  );
  const prParam = params.get("pr") ? Number(params.get("pr")) : undefined;
  const [check, setCheck] = React.useState("");
  const [severity, setSeverity] = React.useState<GovernanceSeverity | "">("");
  const [status, setStatus] = React.useState<SecurityFindingStatus | "all">("open");
  const [scope, setScope] = React.useState<"repo" | "pr" | "all">(prParam ? "pr" : "repo");

  const repos = useGovernedRepos(false);
  const checks = useRepoChecks();
  const filters = {
    repoId,
    check: check || undefined,
    severity: severity || undefined,
    status,
    scope,
    prNumber: scope === "pr" ? prParam : undefined,
  };
  const findings = useQuery({
    queryKey: qk.securityFindings(filters),
    queryFn: () => listSecurityFindings(filters),
  });
  const summary = useQuery({ queryKey: qk.securitySummary, queryFn: getSecuritySummary });

  return (
    <div className="space-y-6">
      <GovernanceHeader description="Secrets, sensitive files, vulnerable dependencies, insecure code, CI/CD and infrastructure risks, personal data and repository settings across your governed repositories." />

      <SummaryTiles summary={summary.data} loading={summary.isLoading} />

      <AiReviewCard />

      <div className="flex flex-wrap items-center gap-2">
        <select
          className={filterSelect}
          aria-label="Repository"
          value={repoId ?? ""}
          onChange={(e) => setRepoId(e.target.value ? Number(e.target.value) : undefined)}
        >
          <option value="">All repositories</option>
          {(repos.data ?? [])
            .filter((r) => r.enabled)
            .map((r) => (
              <option key={r.github_repo_id} value={r.github_repo_id}>
                {r.full_name}
              </option>
            ))}
        </select>
        <select
          className={filterSelect}
          aria-label="Check"
          value={check}
          onChange={(e) => setCheck(e.target.value)}
        >
          <option value="">All checks</option>
          {(checks.data ?? [])
            .filter((c) => c.key !== "agent_config")
            .map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
        </select>
        <select
          className={filterSelect}
          aria-label="Severity"
          value={severity}
          onChange={(e) => setSeverity(e.target.value as GovernanceSeverity | "")}
        >
          <option value="">All severities</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s[0]!.toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
        <select
          className={filterSelect}
          aria-label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as SecurityFindingStatus | "all")}
        >
          {(Object.keys(STATUS_LABEL) as SecurityFindingStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
          <option value="all">Any status</option>
        </select>
        <select
          className={filterSelect}
          aria-label="Where"
          value={scope}
          onChange={(e) => setScope(e.target.value as "repo" | "pr" | "all")}
        >
          <option value="repo">Default branches</option>
          <option value="pr">Pull requests</option>
          <option value="all">Both</option>
        </select>
      </div>

      {findings.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : findings.isError ? (
        <ErrorState error={findings.error} onRetry={() => void findings.refetch()} />
      ) : !findings.data?.length ? (
        <EmptyState
          title={status === "open" ? "No open security findings" : "Nothing matches these filters"}
          hint={
            summary.data?.repos_scanned
              ? "Scans run when a repository is enabled, on every push to its default branch, and on every pull request."
              : "No repository has been scanned yet. Enable a repository under Repositories to run its first scan."
          }
        />
      ) : (
        <FindingsTable findings={findings.data} />
      )}

      <RecentScans repoId={repoId} />
    </div>
  );
}

function SummaryTiles({
  summary,
  loading,
}: {
  summary: Awaited<ReturnType<typeof getSecuritySummary>> | undefined;
  loading: boolean;
}) {
  if (loading) return <Skeleton className="h-20 w-full" />;
  if (!summary) return null;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
      <Tile label="Open findings" value={summary.open_total} />
      {(["critical", "high", "medium", "low"] as const).map((s) => (
        <Tile
          key={s}
          label={s[0]!.toUpperCase() + s.slice(1)}
          value={summary.by_severity[s] ?? 0}
        />
      ))}
      <Tile
        label="Repositories scanned"
        value={summary.repos_scanned}
        sub={summary.last_scan_at ? <When iso={summary.last_scan_at} /> : "never"}
      />
      <Card
        className="grid gap-0.5 p-4"
        title="Security checks call no model; this is the AI pass of pull-request agent-config reviews."
      >
        <span className="text-xs text-muted-foreground">Model cost · 30 days</span>
        <span className="text-2xl font-semibold tabular-nums">
          {formatUsd(summary.model_cost_last_30d_usd)}
        </span>
        <span className="text-xs text-muted-foreground">
          {formatTokens(summary.model_tokens_last_30d)} tokens · {summary.scans_last_30d} scans
        </span>
      </Card>
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: number; sub?: React.ReactNode }) {
  return (
    <Card className="grid gap-0.5 p-4">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-2xl font-semibold tabular-nums">{value.toLocaleString()}</span>
      {sub ? <span className="text-xs text-muted-foreground">{sub}</span> : null}
    </Card>
  );
}

function FindingsTable({ findings }: { findings: SecurityFindingView[] }) {
  const [open, setOpen] = React.useState<number | null>(null);
  return (
    <Card>
      <CardContent className="overflow-x-auto p-0">
        <Table>
          <THead>
            <TR>
              <TH className="w-8" />
              <TH>Severity</TH>
              <TH>Issue</TH>
              <TH>Where</TH>
              <TH>Check</TH>
              <TH>Status</TH>
              <TH>First seen</TH>
            </TR>
          </THead>
          <TBody>
            {findings.map((f) => (
              <React.Fragment key={f.id}>
                <TR
                  className="cursor-pointer align-top"
                  onClick={() => setOpen(open === f.id ? null : f.id)}
                >
                  <TD>
                    {open === f.id ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </TD>
                  <TD>
                    <SeverityBadge severity={f.severity} />
                  </TD>
                  <TD className="max-w-md">
                    <div className="flex flex-wrap items-center gap-2 font-medium">
                      {f.title}
                      <AiChip finding={f} />
                    </div>
                    {f.snippet ? (
                      <code className="mt-1 block truncate text-xs text-muted-foreground">
                        {f.snippet}
                      </code>
                    ) : null}
                  </TD>
                  <TD className="max-w-xs text-sm">
                    <div className="text-xs text-muted-foreground">
                      {f.repo_full_name}
                      {f.scope === "pr" && f.pr_number ? ` · PR #${f.pr_number}` : ""}
                    </div>
                    <code className="break-all text-xs">
                      {f.path}
                      {f.line ? `:${f.line}` : ""}
                    </code>
                  </TD>
                  <TD className="text-sm">{f.check_label}</TD>
                  <TD>
                    <Badge
                      variant={
                        f.status === "open"
                          ? "destructive"
                          : f.status === "fixed" || f.status === "resolved"
                            ? "success"
                            : "secondary"
                      }
                    >
                      {STATUS_LABEL[f.status]}
                    </Badge>
                  </TD>
                  <TD>
                    <When iso={f.first_seen_at} />
                  </TD>
                </TR>
                {open === f.id ? (
                  <TR>
                    <TD colSpan={7} className="bg-muted/20">
                      <FindingDetail finding={f} />
                    </TD>
                  </TR>
                ) : null}
              </React.Fragment>
            ))}
          </TBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function FindingDetail({ finding: f }: { finding: SecurityFindingView }) {
  const qc = useQueryClient();
  const isAdmin = useIsGovernanceAdmin();
  const [note, setNote] = React.useState(f.resolution_note ?? "");
  const triage = useMutation({
    mutationFn: (status: Exclude<SecurityFindingStatus, "fixed">) =>
      updateSecurityFinding(f.id, status, note.trim() || undefined),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["my", "governance", "security"] });
      void qc.invalidateQueries({ queryKey: qk.governedRepos });
      toast.success("Finding updated.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const detail = f.detail ?? {};
  const cves = Array.isArray(detail.cves) ? (detail.cves as string[]) : [];
  const advisories = Array.isArray(detail.advisories) ? (detail.advisories as string[]) : [];

  return (
    <div className="grid gap-3 py-2 text-sm lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="space-y-2">
        <p>{f.rationale}</p>
        {f.ai_reason || f.ai_fix ? (
          <div className="rounded-md border border-border bg-background p-3">
            <div className="mb-1 flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              AI review <AiChip finding={f} />
            </div>
            {f.ai_reason ? <p>{f.ai_reason}</p> : null}
            {f.ai_fix ? (
              <p className="mt-1">
                <span className="font-medium">Suggested fix: </span>
                {f.ai_fix}
              </p>
            ) : null}
            <p className="mt-1 text-xs text-muted-foreground">
              {f.ai_model} ·{" "}
              {formatTokens((f.ai_tokens_prompt ?? 0) + (f.ai_tokens_completion ?? 0))} tokens ·{" "}
              {formatUsd(f.ai_cost_usd)}
            </p>
          </div>
        ) : null}
        {f.fix ? (
          <p>
            <span className="font-medium">How to fix (rule): </span>
            {f.fix}
          </p>
        ) : null}
        {cves.length || advisories.length ? (
          <p className="text-xs text-muted-foreground">
            {[...cves, ...advisories.filter((a) => !cves.includes(a))].slice(0, 8).join(" · ")}
            {typeof detail.fixed_in === "string" ? ` · fixed in ${detail.fixed_in}` : ""}
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Rule <code>{f.rule_id}</code> · last seen <When iso={f.last_seen_at} />
          {f.resolved_by ? ` · ${STATUS_LABEL[f.status].toLowerCase()} by ${f.resolved_by}` : ""}
          {f.resolution_note ? ` — “${f.resolution_note}”` : ""}
        </p>
        {f.github_url ? (
          <a
            href={f.github_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
          >
            Open on GitHub <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>
      {isAdmin ? (
        <div className="space-y-2">
          <textarea
            className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            placeholder="Note (optional) — e.g. why this is a false positive"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label="Triage note"
          />
          <div className="flex flex-wrap gap-2">
            {(["acknowledged", "false_positive", "resolved", "open"] as const)
              .filter((s) => s !== f.status)
              .map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={triage.isPending}
                  onClick={() => triage.mutate(s)}
                  className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50"
                >
                  {s === "open" ? "Reopen" : `Mark ${STATUS_LABEL[s].toLowerCase()}`}
                </button>
              ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

const AI_VERDICT: Record<
  string,
  { label: string; variant: "success" | "secondary" | "warning" | "destructive" }
> = {
  true_positive: { label: "AI: real issue", variant: "destructive" },
  false_positive: { label: "AI: likely false positive", variant: "secondary" },
  needs_review: { label: "AI: needs review", variant: "warning" },
};

function AiChip({ finding: f }: { finding: SecurityFindingView }) {
  if (!f.ai_verdict) return null;
  const v = AI_VERDICT[f.ai_verdict];
  if (!v) return null;
  return (
    <Badge variant={v.variant} title={f.ai_reason ?? undefined}>
      {v.label}
      {f.ai_confidence != null ? ` · ${Math.round(f.ai_confidence * 100)}%` : ""}
    </Badge>
  );
}

/** The AI review switch and per-scan cap (admins), with the model it uses. */
function AiReviewCard() {
  const qc = useQueryClient();
  const isAdmin = useIsGovernanceAdmin();
  const current = useQuery({ queryKey: qk.securityAiReview, queryFn: getSecurityAiReview });
  const [cap, setCap] = React.useState("");
  React.useEffect(() => {
    if (current.data) setCap(String(current.data.max_findings));
  }, [current.data]);
  const save = useMutation({
    mutationFn: (input: { enabled: boolean; max_findings: number }) => setSecurityAiReview(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.securityAiReview });
      toast.success("AI review settings saved.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const rerun = useMutation({
    mutationFn: () => rerunSecurityAiReview(),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["my", "governance", "security"] });
      toast.success(
        `${r.reset} finding${r.reset === 1 ? "" : "s"} queued for a new AI review; ` +
          `${r.scans.length} scan${r.scans.length === 1 ? "" : "s"} started.`,
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });
  if (!current.data) return null;
  const d = current.data;
  const capN = Number(cap);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 text-sm">
      <label className="flex items-center gap-2 font-medium">
        <input
          type="checkbox"
          className="h-4 w-4 accent-[hsl(var(--primary))]"
          checked={d.enabled}
          disabled={!isAdmin || save.isPending}
          onChange={(e) => save.mutate({ enabled: e.target.checked, max_findings: d.max_findings })}
        />
        AI review of findings
      </label>
      <span className="text-muted-foreground">
        {d.model ? (
          <>
            with <span className="font-mono">{d.model}</span> — confirms or dismisses each new
            finding and writes a fix. Critical findings are never auto-dismissed.
          </>
        ) : (
          <>
            No analyzer model is selected; choose one on the{" "}
            <a className="underline underline-offset-4" href="/governance">
              Agent config reviews
            </a>{" "}
            tab.
          </>
        )}
      </span>
      <span className="ml-auto flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Max per scan</span>
        <input
          type="number"
          min={1}
          max={500}
          value={cap}
          disabled={!isAdmin}
          onChange={(e) => setCap(e.target.value)}
          className="h-8 w-20 rounded-md border border-input bg-background px-2 text-sm"
          aria-label="Most findings AI-reviewed per scan"
        />
        {isAdmin && capN !== d.max_findings ? (
          <button
            type="button"
            disabled={!Number.isInteger(capN) || capN < 1 || capN > 500 || save.isPending}
            onClick={() => save.mutate({ enabled: d.enabled, max_findings: capN })}
            className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50"
          >
            Save
          </button>
        ) : null}
        {isAdmin && d.enabled && d.model ? (
          <button
            type="button"
            disabled={rerun.isPending}
            onClick={() => rerun.mutate()}
            title="Clear the AI's verdicts (not anyone's triage) and review those findings again with the current model and prompt."
            className="rounded-md border border-border px-2.5 py-1 text-xs hover:bg-muted disabled:opacity-50"
          >
            {rerun.isPending ? "Starting…" : "Re-review findings"}
          </button>
        ) : null}
      </span>
    </div>
  );
}

const SCAN_KIND: Record<RepoScanView["kind"], string> = {
  full: "Full repository",
  pr: "Pull request",
  history: "Git history",
};

function RecentScans({ repoId }: { repoId?: number }) {
  const scans = useQuery({
    queryKey: qk.repoScans({ repoId }),
    queryFn: () => listRepoScans({ repoId }),
    refetchInterval: (q) =>
      q.state.data?.some((s) => s.status === "queued" || s.status === "running") ? 5000 : false,
  });
  if (scans.isLoading || !scans.data?.length) return null;
  return (
    <div className="space-y-2">
      <h2 className="text-base font-semibold">Recent scans</h2>
      <Card>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <THead>
              <TR>
                <TH>Scan</TH>
                <TH>Repository</TH>
                <TH>Status</TH>
                <TH>Files</TH>
                <TH>Findings</TH>
                <TH>Model / tokens</TH>
                <TH>Cost</TH>
                <TH>When</TH>
              </TR>
            </THead>
            <TBody>
              {scans.data.slice(0, 15).map((s) => (
                <TR key={s.id} className="align-top">
                  <TD className="text-sm">
                    {SCAN_KIND[s.kind]}
                    {s.kind === "pr" && s.pr_number ? (
                      <div className="text-xs text-muted-foreground">
                        #{s.pr_number} {s.pr_title ?? ""}
                      </div>
                    ) : s.requested_by ? (
                      <div className="text-xs text-muted-foreground">by {s.requested_by}</div>
                    ) : null}
                  </TD>
                  <TD className="text-sm">{s.repo_full_name}</TD>
                  <TD>
                    <Badge
                      variant={
                        s.status === "failed"
                          ? "destructive"
                          : s.status === "complete"
                            ? s.partial_error
                              ? "warning"
                              : "success"
                            : "secondary"
                      }
                    >
                      {s.status === "complete" && s.partial_error ? "partial" : s.status}
                    </Badge>
                    {s.last_error || s.partial_error ? (
                      <div className="mt-1 max-w-xs text-xs text-muted-foreground">
                        {s.last_error ?? s.partial_error}
                      </div>
                    ) : null}
                  </TD>
                  <TD className="text-sm tabular-nums">{s.files_scanned.toLocaleString()}</TD>
                  <TD className="text-sm tabular-nums">
                    {s.findings_total}
                    {s.new_findings || s.fixed_findings ? (
                      <div className="text-xs text-muted-foreground">
                        {s.new_findings} new · {s.fixed_findings} fixed
                      </div>
                    ) : null}
                    {s.ai_reviewed ? (
                      <div className="text-xs text-muted-foreground">
                        AI reviewed {s.ai_reviewed} · dismissed {s.ai_dismissed}
                      </div>
                    ) : s.ai_error ? (
                      <div
                        className="max-w-[14rem] text-xs text-muted-foreground"
                        title={s.ai_error}
                      >
                        not AI-reviewed
                      </div>
                    ) : null}
                  </TD>
                  <TD className="text-sm">
                    {s.model ? (
                      <>
                        <div className="font-mono text-xs">{s.model}</div>
                        <div className="text-xs tabular-nums text-muted-foreground">
                          {formatTokens(s.tokens_prompt)} in / {formatTokens(s.tokens_completion)}{" "}
                          out
                        </div>
                      </>
                    ) : (
                      <span
                        className="text-xs text-muted-foreground"
                        title="Security checks are deterministic rules; no model is called."
                      >
                        no model
                      </span>
                    )}
                  </TD>
                  <TD className="text-sm tabular-nums">
                    {formatUsd(s.model_cost_usd)}
                    {s.duration_ms != null ? (
                      <div className="text-xs text-muted-foreground">
                        {(s.duration_ms / 1000).toFixed(1)} s
                      </div>
                    ) : null}
                  </TD>
                  <TD>
                    <When iso={s.completed_at ?? s.created_at} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
